import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type MutableRefObject,
} from "react";
import type {
  OnboardingAnswerUpdates,
  SimpleBusinessSetupPayload,
} from "./types";
import { OnboardingStepActiveContext } from "./use-step-dialog";

export interface StepPayload {
  answers?: OnboardingAnswerUpdates;
  business?: SimpleBusinessSetupPayload;
}

/**
 * Valid data the active step saves when the customer leaves by the checklist.
 *
 * Leaving a step by the checklist used to carry nothing, so a step the customer had
 * filled in was saved as empty and could not be marked finished. The active step
 * publishes a getter here, and the checklist reads it at the moment it navigates, so
 * what is on screen travels with the move.
 */
export const StepPayloadContext = createContext<MutableRefObject<
  (() => StepPayload | undefined) | null
> | null>(null);

/**
 * Publish this step's payload while it is the step on screen.
 *
 * @param payload valid full or partial data, or undefined when nothing can be saved.
 *   Invalid fields must not travel: a rejected jump would prevent the customer from
 *   leaving the step. A valid partial answer can still withdraw a saved confirmation.
 */
export const usePublishStepPayload = (payload: StepPayload | undefined) => {
  const active = useContext(OnboardingStepActiveContext);
  const slot = useContext(StepPayloadContext);
  // Read at navigation time rather than at render time, so the latest keystroke counts
  // without the checklist re-rendering on every one.
  const latest = useRef(payload);
  latest.current = payload;

  useEffect(() => {
    if (!active || !slot) return;
    const read = () => latest.current;
    slot.current = read;
    return () => {
      if (slot.current === read) slot.current = null;
    };
  }, [active, slot]);
};

export const useStepPayloadSlot = () => useContext(StepPayloadContext);
